import {Spinner, IconButton} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useRef, useState} from 'react'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {PencilIcon, XIcon} from '@primer/octicons-react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {isModernPullRequestExperienceEnabled} from '@github-ui/pull-request-feature-flags'
import {
  DockedPanel,
  useDockedPanelActions,
  useDockedPanelFocusRefs,
  useIsDockedPanelCompact,
  useIsDockedPanelOpen,
} from '../../contexts/DockedPanelContext'
import {useDescriptionData} from '../../page-data/loaders/use-description-data'
import {Panel} from './Panel'
import {CompactPanel} from './CompactPanel'
import {DescriptionEditor} from '../overview/DescriptionEditor'
import {useLayoutCanEditTitle} from '../../stores/layout-selectors'

/**
 * Renders the overview side panel for pull requests.
 *
 * Rendered by DockedSidePanelContent in non-compact mode (returns a `<Panel>`)
 * and by CompactSidePanelDialog in compact mode (returns CompactPanel slots).
 * When `updated_pull_request_header` is OFF, falls back to a legacy per-panel
 * `<Dialog>` sidesheet in compact mode.
 */
export function OverviewSidePanel() {
  const isOpen = useIsDockedPanelOpen(DockedPanel.Overview)
  const isCompact = useIsDockedPanelCompact()
  const {closePanel} = useDockedPanelActions()
  const {returnFocusRef} = useDockedPanelFocusRefs()
  // Editing the title has the same permissions as editing the description
  const canEditDescription = useLayoutCanEditTitle()

  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const editButtonRef = useRef<HTMLButtonElement>(null)
  const [isEditing, setIsEditing] = useState(false)

  const {data: description, isLoading} = useDescriptionData()
  /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side */
  const bodyHtml = description?.bodyHtml ? (description.bodyHtml as SafeHTMLString) : null
  const body = description?.body ?? ''

  const updatedHeader = isModernPullRequestExperienceEnabled()
  const panelLabel = updatedHeader ? 'Description' : 'Overview'
  const closeAriaLabel = updatedHeader ? 'Close description panel' : 'Close overview panel'

  if (!isOpen) return null

  const canShowEditButton = isFeatureEnabled('pull_request_overview_panel_edit_description')

  const editButton =
    canShowEditButton && !isLoading && canEditDescription ? (
      <IconButton
        ref={editButtonRef}
        icon={PencilIcon}
        aria-label="Edit description"
        variant="invisible"
        size="medium"
        onClick={() => setIsEditing(true)}
        inactive={isEditing}
        aria-disabled={isEditing}
      />
    ) : null

  const content = isLoading ? (
    <div className="d-flex flex-justify-center">
      <Spinner srText="Loading description" />
    </div>
  ) : (
    <DescriptionEditor
      bodyHtml={bodyHtml}
      body={body}
      isEditing={isEditing}
      onEditingChange={setIsEditing}
      returnFocusRef={editButtonRef}
    />
  )

  if (!isCompact) {
    return (
      <Panel panelName={DockedPanel.Overview} headerActions={editButton}>
        <Panel.Body>{content}</Panel.Body>
      </Panel>
    )
  }

  if (!updatedHeader) {
    return (
      <Dialog
        initialFocusRef={closeButtonRef}
        position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
        returnFocusRef={returnFocusRef}
        onClose={closePanel}
        renderHeader={({dialogLabelId}) => (
          <Dialog.Header>
            <div className="d-flex flex-row flex-justify-between flex-items-center width-full pl-2">
              <Dialog.Title id={dialogLabelId}>{panelLabel}</Dialog.Title>
              <div className="d-flex flex-items-center">
                {editButton}
                <IconButton
                  ref={closeButtonRef}
                  aria-label={closeAriaLabel}
                  icon={XIcon}
                  variant="invisible"
                  onClick={closePanel}
                />
              </div>
            </div>
          </Dialog.Header>
        )}
      >
        {content}
      </Dialog>
    )
  }

  return (
    <>
      {editButton ? <CompactPanel.HeaderActions>{editButton}</CompactPanel.HeaderActions> : null}
      <CompactPanel.Content>{content}</CompactPanel.Content>
    </>
  )
}
